import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ChefHat, Search, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { fmtMVR } from "@/lib/api";
import { toast } from "sonner";

const STATUS_COLORS = {
    pending: {
        label: "Pending",
        bg: "bg-pz-warning/15",
        text: "text-pz-warning",
        ring: "ring-pz-warning/30",
    },
    confirmed: {
        label: "Confirmed",
        bg: "bg-blue-500/10",
        text: "text-blue-600",
        ring: "ring-blue-500/30",
    },
    ready: {
        label: "Ready",
        bg: "bg-pz-success/15",
        text: "text-pz-success",
        ring: "ring-pz-success/30",
    },
    out_for_delivery: {
        label: "Out for Delivery",
        bg: "bg-orange-500/10",
        text: "text-orange-600",
        ring: "ring-orange-500/30",
    },
    delivery_arrived: {
        label: "Delivery Arrived",
        bg: "bg-purple-500/10",
        text: "text-purple-600",
        ring: "ring-purple-500/30",
    },
    completed: {
        label: "Completed",
        bg: "bg-pz-textMuted/15",
        text: "text-pz-textMuted",
        ring: "ring-pz-textMuted/30",
    },
};

const ORDER_TYPE_LABELS = {
    delivery: "Delivery",
    takeaway: "Takeaway-Pickup",
    pickup: "Takeaway-Pickup",
    dine_in: "Dine-in",
    dinein: "Dine-in",
};

const DEFAULT_STATUS = {
    label: "Processing",
    bg: "bg-pz-textMuted/15",
    text: "text-pz-textMuted",
    ring: "ring-pz-textMuted/30",
};

export default function TrackPage() {
    const [num, setNum] = useState("");
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const handleSearch = async (e) => {
        e?.preventDefault();

        const cleaned = num.trim().replace(/^#?/, "").toUpperCase();

        if (!cleaned) return toast.error("Enter an order number");

        setLoading(true);
        setError("");
        setOrder(null);

        try {
            const res = await api.get(`/orders/track/${cleaned}`);
            setOrder(res.data);
        } catch (err) {
            setError("We couldn't find that order. Double-check the number.");
        } finally {
            setLoading(false);
        }
    };

    const status = order
        ? STATUS_COLORS[order.status] || DEFAULT_STATUS
        : DEFAULT_STATUS;

    const orderTypeLabel = order
        ? ORDER_TYPE_LABELS[order.order_type] || order.order_type
        : "";

    return (
        <div
            className="min-h-screen bg-pz-bg paper-grain"
            data-testid="track-page"
        >
            <header className="px-4 sm:px-6 py-5 max-w-3xl mx-auto flex items-center gap-3">
                <Link to="/" className="text-pz-text hover:text-pz-primary">
                    <ArrowLeft size={22} />
                </Link>

                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-pz-primary text-white grid place-items-center">
                        <ChefHat size={16} />
                    </div>

                    <span className="font-display text-xl tracking-wide text-pz-text">
                        Track Order
                    </span>
                </div>
            </header>

            <main className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
                <div className="text-center mb-8 animate-float-up">
                    <p className="uppercase text-xs tracking-[0.4em] text-pz-primary font-semibold mb-2">
                        — Order Status —
                    </p>

                    <h1 className="font-display text-4xl sm:text-5xl text-pz-text">
                        Where's my food?
                    </h1>

                    <p className="text-pz-textMuted mt-2">
                        Enter your order number, for example{" "}
                        <span className="font-mono">PZ-001</span>
                    </p>
                </div>

                <form
                    onSubmit={handleSearch}
                    className="flex gap-2 bg-white rounded-full border border-pz-border p-1.5 mb-8 shadow-sm"
                    data-testid="track-form"
                >
                    <Input
                        value={num}
                        onChange={(e) => setNum(e.target.value)}
                        placeholder="PZ-001"
                        data-testid="track-input"
                        className="border-0 focus-visible:ring-0 bg-transparent text-pz-text font-mono uppercase"
                    />

                    <Button
                        type="submit"
                        disabled={loading}
                        data-testid="track-submit"
                        className="rounded-full bg-pz-primary hover:bg-pz-primaryHover text-white px-5"
                    >
                        <Search size={16} className="mr-1" />
                        {loading ? "..." : "Find"}
                    </Button>
                </form>

                {error && (
                    <div className="text-center text-pz-textMuted bg-white border border-pz-border rounded-2xl p-6">
                        {error}
                    </div>
                )}

                {order && (
                    <div
                        data-testid="track-result"
                        className="bg-white rounded-2xl border border-pz-border overflow-hidden animate-float-up"
                    >
                        <div className="bg-pz-text text-white p-6 flex items-center justify-between gap-4">
                            <div>
                                <p className="text-xs uppercase tracking-widest text-white/60">
                                    Order
                                </p>

                                <div className="font-display text-4xl">
                                    #{order.order_number}
                                </div>
                            </div>

                            <div
                                className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full ring-1 ${status.bg} ${status.text} ${status.ring}`}
                            >
                                <Clock size={14} />

                                <span className="text-xs font-bold uppercase tracking-wider">
                                    {status.label}
                                </span>
                            </div>
                        </div>

                        <div className="p-6 space-y-4">
                            <div className="grid sm:grid-cols-2 gap-4 text-sm">
                                <div>
                                    <p className="text-xs uppercase text-pz-textMuted font-semibold">
                                        Customer
                                    </p>

                                    <p className="text-pz-text">
                                        {order.customer_name}
                                    </p>
                                </div>

                                <div>
                                    <p className="text-xs uppercase text-pz-textMuted font-semibold">
                                        Type
                                    </p>

                                    <p className="text-pz-text">
                                        {orderTypeLabel}
                                    </p>
                                </div>
                            </div>

                            <div className="border-t border-pz-border pt-3 space-y-1.5">
                                {order.items?.map((i) => (
                                    <div
                                        key={i.id}
                                        className="flex justify-between text-sm"
                                    >
                                        <span className="text-pz-text">
                                            <b>{i.quantity}×</b> {i.name}
                                        </span>

                                        <span className="text-pz-textMuted">
                                            {fmtMVR(i.price * i.quantity)}
                                        </span>
                                    </div>
                                ))}
                            </div>

                            <div className="flex items-center justify-between pt-3 border-t border-pz-border">
                                <span className="text-xs uppercase tracking-wider text-pz-textMuted font-semibold">
                                    Total
                                </span>

                                <span className="font-display text-2xl text-pz-primary">
                                    {fmtMVR(order.total)}
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}
